import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import logo from "../assets/images/logo-round-purple.svg";

/* Logo intro animation — shown on the first visit per tab session,
 * then skipped on subsequent page loads (see IntroContext).
 *
 * Keyframe shape: fast fade-in → hold → smooth fade-out. Tightened
 * from the original 2.25s (felt unnecessarily long for a B2B
 * marketing site) to 1.2s — fast enough that the user isn't waiting,
 * slow enough that the brand mark registers.
 *
 * Per-segment timing functions (animation-timing-function inside the
 * keyframe) override the default `ease`, which would compress a
 * slow-fast-slow curve into the short fade-out window and make the
 * fade lurch. Instead:
 *   • 0→18%   ease-in   — gentle ramp into full opacity
 *   • 18→75%  linear    — flat hold (timing irrelevant since value
 *                         doesn't change, but explicit for clarity)
 *   • 75→100% ease-out  — fast at start, decelerating into 0; the
 *                         end of the fade feels like settling rather
 *                         than dropping off a cliff
 *
 * Fade-out window is 25% of the animation = 300ms — long enough that
 * the eye registers it as smooth, short enough that the intro doesn't
 * drag. The 75% keystone is mirrored by the IntroOverlay's own
 * overlayFadeOut keyframe in Layout.js (logo + white background fade
 * together to reveal the page). Retime both together. */
const fadeInOut = keyframes`
  0%   { opacity: 0; animation-timing-function: ease-in; }
  18%  { opacity: 1; animation-timing-function: linear; }
  75%  { opacity: 1; animation-timing-function: ease-out; }
  100% { opacity: 0; }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100svh;
  min-height: 100dvh;
  opacity: 0;
  /* Animation only starts once the SVG has finished loading. Before
     $loaded flips, the container stays at opacity:0 — no flash of
     the white IntroOverlay behind an empty <img> element. */
  ${({ $loaded }) => $loaded && css`
    animation: ${fadeInOut} 1.2s forwards;
  `}
`;

const LogoImage = styled.img`
  width: 40%;
  max-width: 600px;
  height: auto;
  user-select: none;
`;

const LogoAnimation = () => {
  const [loaded, setLoaded] = useState(false);

  /* Safety net: `onLoad` doesn't always fire for already-cached
     images (browsers differ). If the image hasn't signaled load
     within 120ms, assume it's cached and start the animation
     anyway. Prevents the intro from stalling on fast reloads. */
  useEffect(() => {
    if (loaded) return;
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, [loaded]);

  return (
    <LogoContainer $loaded={loaded}>
      <LogoImage
        src={logo}
        draggable="false"
        alt="Soquelec"
        onLoad={() => setLoaded(true)}
      />
    </LogoContainer>
  );
};

export default LogoAnimation;
